/**-----------------------  HELPER FUNCTIONS -------------------*/

import { I18n } from '../components';

import {
  EVENT_TYPE_MEDICATION,
  EVENT_TYPE_MEDICAL_APPOINTMENT,
  EVENT_TYPE_SOCIAL,
  EVENT_TYPE_OTHER,
} from '../../constants/eventTypes';

/**
 * Maps the repeat values from the canonical/server event object to the control used in the UI, that's only one
 * dropdown or listview. UI values ATM: Daily, Weekly, 2 Weeks, Montly, Yearly.
 * Mapping [frequency, interval]:
 * Daily (11) = [0, 1], Weekly (21) = [1, 1], 2 Weeks (22) = [1, 2], Montly (31) = [2, 1], Yearly (41) = [3, 1]
 * @param frequency
 * @param interval
 * @returns {null}
 */
function mapCanonicalRepeatToUIRepeat(frequency, interval) {
  if (!frequency && !interval) return -1;
  //repeat.frequency: [0: daily, 1: weekly, 2: monthly, 3: year]
  if (frequency && !interval) interval = 1;
  return (frequency + 1) * 10 + interval;
}

/**
 * mapCanonicalRepeatToUIRepeat reversed
 * @param frequency
 * @param interval
 * @returns {*}
 */
function mapUIRepeatToCanonicalRepeat(repeat) {
  if (!repeat || repeat.repeat === -1) return null;
  let frequency = Math.floor(repeat.repeat / 10) - 1;
  let interval = repeat.repeat % 10;
  let fixedUpEnd = null;
  if (repeat.endDate) {
    fixedUpEnd = new Date(repeat.endDate);
    fixedUpEnd.setSeconds(0, 0);
    //Do we need this??
    fixedUpEnd = fixedUpEnd.toISOString();
  }
  let rep = {
    frequency: frequency,
    interval: interval,
    endDate: fixedUpEnd,
  };
  return rep;
}

export function getUIRepeatText(repeat) {
  const repeatFreq = repeat.repeat;
  let formattedRepeat;
  if (!repeatFreq || repeatFreq === -1) {
    formattedRepeat = I18n.t('calendar.repeatNone');
  } else if (repeatFreq === 11) {
    formattedRepeat = I18n.t('calendar.repeatDaily');
  } else if (repeatFreq === 21) {
    formattedRepeat = I18n.t('calendar.repeatWeekly');
  } else if (repeatFreq === 22) {
    formattedRepeat = I18n.t('calendar.repeat2Weeks');
  } else if (repeatFreq === 31) {
    formattedRepeat = I18n.t('calendar.repeatMonthly');
  } else if (repeatFreq === 41) {
    formattedRepeat = I18n.t('calendar.repeatYearly');
  } else {
    formattedRepeat = I18n.t('calendar.repeatUnknown');
  }
  if (repeat.endDate) {
    formattedRepeat =
      formattedRepeat +
      '\n' +
      I18n.t('calendar.repeatEndsDate', {
        dateStr: repeat.endDate.toLocaleDateString(),
      });
  }
  return formattedRepeat;
}

export function getUIRepeatValues() {
  return [
    { label: I18n.t('calendar.repeatNone'), id: -1 },
    { label: I18n.t('calendar.repeatDaily'), id: 11 },
    { label: I18n.t('calendar.repeatWeekly'), id: 21 },
    { label: I18n.t('calendar.repeat2Weeks'), id: 22 },
    { label: I18n.t('calendar.repeatMonthly'), id: 31 },
    { label: I18n.t('calendar.repeatYearly'), id: 41 },
  ];
}

export function getUIEventTypeNames() {
  return [
    {
      label: I18n.t(`calendar.eventTypes.${EVENT_TYPE_MEDICATION}`),
      id: EVENT_TYPE_MEDICATION,
    },
    {
      label: I18n.t(`calendar.eventTypes.${EVENT_TYPE_MEDICAL_APPOINTMENT}`),
      id: EVENT_TYPE_MEDICAL_APPOINTMENT,
    },
    {
      label: I18n.t(`calendar.eventTypes.${EVENT_TYPE_SOCIAL}`),
      id: EVENT_TYPE_SOCIAL,
    },
    {
      label: I18n.t(`calendar.eventTypes.${EVENT_TYPE_OTHER}`),
      id: EVENT_TYPE_OTHER,
    },
  ];
}

export function getUIReminderValues() {
  return [
    { label: I18n.t('calendar.reminderNone'), id: -1 },
    { label: I18n.t('calendar.reminder5MinutesBefore'), id: 300 },
    { label: I18n.t('calendar.reminder15MinutesBefore'), id: 900 },
    { label: I18n.t('calendar.reminder30MinutesBefore'), id: 1800 },
    { label: I18n.t('calendar.reminder1HourBefore'), id: 3600 },
    { label: I18n.t('calendar.reminder2HoursBefore'), id: 7200 },
    { label: I18n.t('calendar.reminder1DayBefore'), id: 86400 },
  ];
}

export function getUIReminderText(reminder) {
  if (!reminder || reminder === -1) return I18n.t('calendar.reminderNone');
  if (reminder === 300) return I18n.t('calendar.reminder5MinutesBefore');
  if (reminder === 900) return I18n.t('calendar.reminder15MinutesBefore');
  if (reminder === 1800) return I18n.t('calendar.reminder30MinutesBefore');
  if (reminder === 3600) return I18n.t('calendar.reminder1HourBefore');
  if (reminder === 7200) return I18n.t('calendar.reminder2HoursBefore');
  if (reminder === 86400) return I18n.t('calendar.reminder1DayBefore');
}

export function getUIAssigneeName(assignee, followers) {
  const follower = assignee && followers && followers[assignee];
  if (!follower) return I18n.t('calendar.none');

  return follower.friendlyName;
}

export function formatEventObjForUI(eventObj, editingFiles, followers) {
  const formattedEvent = {
    ...eventObj,
    startDate: eventObj.startDate !== null ? new Date(eventObj.startDate) : null,
    endDate: eventObj.endDate !== null ? new Date(eventObj.endDate) : null,
    repeat: {
      repeat: eventObj.repeat
        ? mapCanonicalRepeatToUIRepeat(eventObj.repeat.frequency, eventObj.repeat.interval)
        : -1,
      endDate:
        eventObj.repeat && eventObj.repeat.endDate ? new Date(eventObj.repeat.endDate) : null,
    },
    files: editingFiles,
  };

  formattedEvent.reminderUIString = getUIReminderText(formattedEvent.reminder);
  formattedEvent.repeatUIString = getUIRepeatText(formattedEvent.repeat);
  formattedEvent.assigneeNameUIString = getUIAssigneeName(formattedEvent.assignee, followers);

  return formattedEvent;
}

export function formatEventObjBeforeSaving(eventObj, eventId = undefined) {
  let fixedUpEnd = new Date(eventObj.endDate);
  fixedUpEnd.setSeconds(0, 0);
  let fixedUpStart = new Date(eventObj.startDate);
  fixedUpStart.setSeconds(0, 0);
  const formattedEventObj = {
    ...eventObj,
    eventId: eventId,
    location: eventObj?.location?.address ? eventObj.location : null,
    startDate: fixedUpStart,
    endDate: fixedUpEnd,
    repeat: mapUIRepeatToCanonicalRepeat(eventObj.repeat),
    reminder: eventObj.reminder || null,
    transportation: eventObj.transportation || null,
    notes: eventObj.notes || null,
  };
  return formattedEventObj;
}

// if a date is passed in then use the hours and minutes from that datetime obj
// otherwise set it to 1 hour after start
export function defaultEndDateFromStart(startDate, endDateForTime) {
  let defaultEnd;
  if (endDateForTime) {
    defaultEnd = new Date(startDate.getTime());
    defaultEnd.setHours(endDateForTime.getHours());
    defaultEnd.setMinutes(endDateForTime.getMinutes());
  } else {
    defaultEnd = new Date(startDate.getTime() + 60 * 60 * 1000);
  }
  return defaultEnd;
}
